import React, {useCallback, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {Link as RouterLink, useNavigate, useParams} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {DateTime} from 'luxon';
import PageTitle from '../shared/PageTitle';
import BackButton from '../shared/BackButton';
import {deleteAccount, editProfile, getAdminProfile} from '../../api/adminAPI';
import {Roles} from '../../enums';
import {Profile} from '../../interfaces';
import ProfilesTable from './ProfilesTable';
import AddEmailModal from './AddEmailModal';
import {validateDateOfBirth} from '../../services/dateService';

const DATE_FORMAT = 'yyyy-M-d';

const seedDate = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toFormat(DATE_FORMAT) : value;
};

interface FieldItem {
  key: string;
  label: React.ReactNode;
  value: string | number;
  onChange?: (value: string) => void;
  error?: string;
  placeholder?: string;
  action?: React.ReactNode;
}

const AdminProfileDetail: React.FC = () => {
  const {t} = useTranslation();
  const {profileId} = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [pending, setPending] = useState(true);

  // Editable fields (backed by the adminProfileEdit mutation).
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [dateError, setDateError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const na = t('general.notAvailable') as string;

  const formatDate = useCallback(
    (value?: string | null) =>
      value ? DateTime.fromISO(value).toFormat(DATE_FORMAT) : na,
    [na],
  );

  const fetchData = useCallback(async () => {
    const id = Number(profileId);
    if (isNaN(id)) {
      return;
    }
    setPending(true);
    try {
      const data = await getAdminProfile(id);
      setProfile(data);
    } catch (e) {
      console.error(e);
    } finally {
      setPending(false);
    }
  }, [profileId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Seed the editable fields whenever the profile (re)loads.
  useEffect(() => {
    setFirstName(profile?.firstName ?? '');
    setLastName(profile?.lastName ?? '');
    setDateOfBirth(seedDate(profile?.dateOfBirth));
    setDateError('');
  }, [profile]);

  const handleSave = useCallback(async () => {
    if (!profile?.id) {
      return;
    }
    const trimmedDob = dateOfBirth.trim();
    if (trimmedDob && !validateDateOfBirth(trimmedDob)) {
      setDateError(
        t('admin.editProfile.invalidDateFormat', {
          dateFormat: DATE_FORMAT,
        }) as string,
      );
      return;
    }
    setDateError('');
    setSaveError('');
    setSaving(true);
    try {
      await editProfile({
        id: profile.id,
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        dateOfBirth: trimmedDob || null,
      });
      await fetchData();
    } catch (e) {
      console.error(e);
      setSaveError(t('admin.editProfile.saveError') as string);
    } finally {
      setSaving(false);
    }
  }, [profile?.id, firstName, lastName, dateOfBirth, fetchData, t]);

  const handleDelete = useCallback(async () => {
    if (!profile?.id) {
      return;
    }
    const confirmed = window.confirm(
      t('admin.profileDetail.deleteAccountConfirm') as string,
    );
    if (!confirmed) {
      return;
    }
    setDeleteError('');
    setDeleting(true);
    try {
      await deleteAccount(profile.id);
      navigate('/admin/profiles');
    } catch (e) {
      console.error(e);
      setDeleteError(t('admin.profileDetail.deleteAccountError') as string);
      setDeleting(false);
    }
  }, [profile?.id, navigate, t]);

  // Flatten the child profiles across all of this profile's child families.
  const childProfiles = (profile?.childFamilies ?? []).flatMap(
    family => family.childProfiles ?? [],
  );

  // The parent profile of this profile's family.
  const parentProfile = profile?.parentFamily?.parentProfile;

  // Whether any editable field differs from the loaded profile values.
  const isDirty =
    !!profile &&
    (firstName !== (profile.firstName ?? '') ||
      lastName !== (profile.lastName ?? '') ||
      dateOfBirth !== seedDate(profile.dateOfBirth));

  // Editable and read-only fields shown alongside each other in the panel.
  const fieldItems: FieldItem[] = profile
    ? [
        {
          key: 'id',
          label: t('admin.shopifySubscriptionsTable.columns.id'),
          value: profile.id ?? na,
        },
        {
          key: 'firstName',
          label: t('profile.firstName'),
          value: firstName,
          onChange: setFirstName,
        },
        {
          key: 'lastName',
          label: t('profile.lastName'),
          value: lastName,
          onChange: setLastName,
        },
        {
          key: 'dateOfBirth',
          label: t('admin.editProfile.dateOfBirthLabel', {
            dateFormat: DATE_FORMAT,
          }),
          value: dateOfBirth,
          onChange: setDateOfBirth,
          error: dateError,
          placeholder: DATE_FORMAT,
        },
        {
          key: 'email',
          label: t('general.email'),
          value: profile.email || na,
          action:
            !profile.email && profile.id != null ? (
              <AddEmailModal profileId={profile.id} onCreated={fetchData} />
            ) : undefined,
        },
        {
          key: 'role',
          label: t('admin.shopifySubscriptionDetail.fields.role'),
          value: profile.role || na,
        },
        {
          key: 'gender',
          label: t('admin.profileDetail.fields.gender'),
          value: profile.gender || na,
        },
        {
          key: 'position',
          label: t('profile.position'),
          value: profile.position || na,
        },
        {
          key: 'shot',
          label: t('profile.shot'),
          value: profile.shot || na,
        },
        {
          key: 'birthYear',
          label: t('admin.profileDetail.fields.birthYear'),
          value: profile.birthYear ?? na,
        },
        {
          key: 'teamName',
          label: t('admin.profileDetail.fields.teamName'),
          value: profile.teamName || na,
        },
        {
          key: 'country',
          label: t('admin.profileDetail.fields.country'),
          value: profile.country || na,
        },
        {
          key: 'stateOrProvince',
          label: t('admin.profileDetail.fields.stateOrProvince'),
          value: profile.stateOrProvince || na,
        },
        {
          key: 'membershipLevel',
          label: t('membership.membershipLevel'),
          value: profile.membershipLevel || na,
        },
        {
          key: 'membershipTrialEndDate',
          label: t('admin.profileDetail.fields.membershipTrialEndDate'),
          value: formatDate(profile.membershipTrialEndDate),
        },
        {
          key: 'lastSession',
          label: t('admin.shopifySubscriptionDetail.fields.lastSession'),
          value: formatDate(profile.lastSessionDate),
        },
        {
          key: 'pendingParentalConsent',
          label: t('admin.profileDetail.fields.pendingParentalConsent'),
          value: (profile.pendingParentalConsent
            ? t('general.yes')
            : t('general.no')) as string,
        },
        {
          key: 'deletedAt',
          label: t('admin.profileDetail.fields.deletedAt'),
          value: formatDate(profile.deletedAt),
        },
      ]
    : [];

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {
            pageName: t('pages.profileDetail', {id: profile?.id ?? ''}),
          })}
        </title>
      </Helmet>
      <Box>
        <PageTitle>
          {t('pages.profileDetail', {id: profile?.id ?? ''})}
        </PageTitle>
        <BackButton navigationUrl="/admin/profiles">
          {t('pages.profiles')}
        </BackButton>
        {pending && !profile && (
          <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
            <CircularProgress />
          </Box>
        )}
        {profile && (
          <>
            <Paper sx={{p: 2, mt: 2}}>
              {saveError && (
                <Alert
                  severity="error"
                  sx={{mb: 2}}
                  onClose={() => setSaveError('')}>
                  {saveError}
                </Alert>
              )}
              {deleteError && (
                <Alert
                  severity="error"
                  sx={{mb: 2}}
                  onClose={() => setDeleteError('')}>
                  {deleteError}
                </Alert>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mb: 2,
                }}>
                {(profile.role === Roles.player ||
                  profile.role === Roles.pro) && (
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to={`/admin/profiles/${profile.id}/playersessions`}>
                    {t('pages.playerSessions')}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  disabled={deleting}>
                  {t('admin.profileDetail.deleteAccount')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !isDirty}>
                  {t('general.save')}
                </Button>
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                {fieldItems.map(item => (
                  <Box
                    key={item.key}
                    sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={item.label}
                      value={item.value}
                      placeholder={item.placeholder}
                      error={!!item.error}
                      helperText={item.error || ''}
                      onChange={
                        item.onChange
                          ? e => item.onChange?.(e.target.value)
                          : undefined
                      }
                      InputProps={
                        item.onChange
                          ? undefined
                          : {
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <LockIcon
                                    fontSize="small"
                                    sx={{color: 'text.secondary'}}
                                  />
                                </InputAdornment>
                              ),
                            }
                      }
                    />
                    {item.action && (
                      <Box sx={{flexShrink: 0}}>{item.action}</Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>

            <Typography variant="h5" sx={{mt: 3, mb: 1}}>
              {t('pages.shopifySubscriptions')}
            </Typography>
            {profile.shopifySubscription ? (
              <Paper sx={{p: 2}}>
                <Typography variant="body1" sx={{mb: 1}}>
                  <strong>
                    {t('admin.shopifySubscriptionsTable.columns.id')}:
                  </strong>{' '}
                  {profile.shopifySubscription.id}
                </Typography>
                <Typography variant="body1" sx={{mb: 1}}>
                  <strong>{t('general.status')}:</strong>{' '}
                  {profile.shopifySubscription.status || na}
                </Typography>
                <Typography variant="body1" sx={{mb: 2}}>
                  <strong>
                    {t('admin.shopifySubscriptionsTable.columns.plan')}:
                  </strong>{' '}
                  {profile.shopifySubscription.plan || na}
                </Typography>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to={`/admin/shopify-subscriptions/${profile.shopifySubscription.id}`}>
                  {t('admin.profileDetail.viewSubscription')}
                </Button>
              </Paper>
            ) : (
              <Typography variant="body1" sx={{mb: 1}}>
                {na}
              </Typography>
            )}

            <Typography variant="h5" sx={{mt: 3, mb: 1}}>
              {t('admin.profileDetail.parentProfiles')}
            </Typography>
            {parentProfile ? (
              <ProfilesTable profiles={[parentProfile]} height={400} />
            ) : (
              <Typography variant="body1" sx={{mb: 1}}>
                {na}
              </Typography>
            )}

            <Typography variant="h5" sx={{mt: 3, mb: 1}}>
              {t('admin.profileDetail.childProfiles')}
            </Typography>
            {childProfiles.length > 0 ? (
              <ProfilesTable profiles={childProfiles} height={400} />
            ) : (
              <Typography variant="body1" sx={{mb: 1}}>
                {na}
              </Typography>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default AdminProfileDetail;
